/**
 * CaseFlow+AI - Feedback Buttons
 * Renders bug report and improvement suggestion buttons in the topbar
 * Always visible after login, on all routes, both themes
 * Responsive: text on desktop, icons only on mobile
 */
import React, { useState } from 'react';
import { Bug, Lightbulb } from 'lucide-react';
import { useTheme } from '../../theme/ThemeProvider';
import { useTranslation } from '../../i18n/LanguageProvider';
import { BugReportModal } from './BugReportModal';
import { ImprovementSuggestionModal } from './ImprovementSuggestionModal';

export function FeedbackButtons() {
  const { theme } = useTheme();
  const { t, language } = useTranslation();
  const [isBugReportOpen, setIsBugReportOpen] = useState(false);
  const [isImprovementOpen, setIsImprovementOpen] = useState(false);

  const buttonBaseClass = `
    inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg
    border transition-all duration-200
    hover:shadow-md hover:-translate-y-0.5
    focus:outline-none focus:ring-2 focus:ring-offset-2
    ${theme === 'dark'
      ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 focus:ring-gray-500'
      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-blue-500'
    }
  `;

  const iconOnlyClass = `
    inline-flex items-center justify-center p-2 text-sm font-medium rounded-lg
    border transition-all duration-200
    hover:shadow-md hover:-translate-y-0.5
    focus:outline-none focus:ring-2 focus:ring-offset-2
    ${theme === 'dark'
      ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 focus:ring-gray-500'
      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-blue-500'
    }
  `;

  return (
    <>
      {/* Bug Report Button */}
      <div className="relative" title={language === 'sv' ? 'Rapportera bugg' : 'Report Bug'}>
        {/* Desktop: icon + text */}
        <button
          onClick={() => setIsBugReportOpen(true)}
          className={`${buttonBaseClass} hidden md:inline-flex`}
          aria-label={language === 'sv' ? 'Rapportera bugg' : 'Report Bug'}
        >
          <Bug className="h-4 w-4 text-red-500" />
          <span>{language === 'sv' ? 'Rapportera bugg' : 'Report Bug'}</span>
        </button>

        {/* Mobile: icon only */}
        <button
          onClick={() => setIsBugReportOpen(true)}
          className={`${iconOnlyClass} md:hidden`}
          aria-label={language === 'sv' ? 'Rapportera bugg' : 'Report Bug'}
        >
          <Bug className="h-4 w-4 text-red-500" />
        </button>
      </div>

      {/* Improvement Suggestion Button */}
      <div className="relative" title={language === 'sv' ? 'Förslag till förbättring' : 'Improvement Suggestion'}>
        {/* Desktop: icon + text */}
        <button
          onClick={() => setIsImprovementOpen(true)}
          className={`${buttonBaseClass} hidden md:inline-flex`}
          aria-label={language === 'sv' ? 'Förslag till förbättring' : 'Improvement Suggestion'}
        >
          <Lightbulb className="h-4 w-4 text-yellow-500" />
          <span>{language === 'sv' ? 'Förslag' : 'Suggestion'}</span>
        </button>

        {/* Mobile: icon only */}
        <button
          onClick={() => setIsImprovementOpen(true)}
          className={`${iconOnlyClass} md:hidden`}
          aria-label={language === 'sv' ? 'Förslag till förbättring' : 'Improvement Suggestion'}
        >
          <Lightbulb className="h-4 w-4 text-yellow-500" />
        </button>
      </div>

      {/* Modals */}
      <BugReportModal
        isOpen={isBugReportOpen}
        onClose={() => setIsBugReportOpen(false)}
      />

      <ImprovementSuggestionModal
        isOpen={isImprovementOpen}
        onClose={() => setIsImprovementOpen(false)}
      />
    </>
  );
}
